import React, { useEffect, useMemo, useState } from "react";
import { changes as changeOptions, months } from "../../utils/Helpers";
import { generateInvoice } from "../../services";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, RefreshCcw, Trash2, X, AlertTriangle } from "lucide-react";

export default function AddInvoiceModal({
  setInvoices,
  invoiceNo,
  month,
  Year,
  memberName,
  change,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedChangeKey, setSelectedChangeKey] = useState("0");
  const [amount, setAmount] = useState("");
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const selectedKeys = useMemo(() => new Set(entries.map((e) => String(e.change))), [entries]);
  const availableChanges = useMemo(
    () => changeOptions.filter((c) => !selectedKeys.has(String(c.key))),
    [selectedKeys]
  );

  const openModal = () => {
    setErrorMsg("");
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsConfirmOpen(false);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const handleAddEntry = () => {
    setErrorMsg("");
    const amt = Number(amount);
    if (selectedChangeKey === "0") return;
    if (!Number.isFinite(amt) || amount === "") return;

    setEntries((prev) => [...prev, { change: selectedChangeKey, amount: amt }]);
    setSelectedChangeKey("0");
    setAmount("");
  };

  const handleRemoveEntry = (index) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateGenerate = async () => {
    if (entries.length === 0 || isLoading) return;
    setErrorMsg("");

    const changesPayload = entries.reduce((acc, entry) => {
      acc[String(entry.change)] = Number(entry.amount);
      return acc;
    }, {});

    const invoiceData = {
      invoiceNo,
      isRegenerate: true,
      month,
      year: Year,
      changes: changesPayload,
    };

    setIsLoading(true);
    try {
      const response = await generateInvoice(invoiceData);
      if (response?.data?.Message !== "Success") {
        setErrorMsg(response?.data?.Message || "Failed to regenerate invoice.");
        return;
      }

      const newInvoices = response?.data?.Result?.Invoices || [];
      setInvoices((prevInvoices) =>
        (prevInvoices || []).map((inv) => {
          const updated = newInvoices.find((n) => n.InvoiceNo === inv.InvoiceNo);
          return updated ? { ...inv, ...updated } : inv;
        })
      );

      setEntries([]);
      setSelectedChangeKey("0");
      setAmount("");
      setIsConfirmOpen(false);
      setIsOpen(false);
    } catch (error) {
      console.error("Error generating invoice", error);
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const canAdd = selectedChangeKey !== "0" && amount !== "" && Number.isFinite(Number(amount));
  const canRegenerate = entries.length > 0 && !isLoading;

  const changeLabelByKey = useMemo(() => {
    const map = new Map(changeOptions.map((c) => [String(c.key), c.label]));
    return (k) => map.get(String(k)) || String(k);
  }, []);

  const totalDelta = useMemo(
    () => entries.reduce((sum, e) => sum + Number(e.amount || 0), 0),
    [entries]
  );
  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-bold text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
      >
        <RefreshCcw className="w-4 h-4" />
        Regenerate
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />

            <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 24, scale: 0.98 }}
                transition={{ type: "spring", damping: 24, stiffness: 280 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-3xl overflow-hidden rounded-3xl border border-gray-200 dark:border-blue-gray-900 bg-white dark:bg-[#0c0c0c] shadow-2xl"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b border-gray-100 dark:border-blue-gray-900 px-6 py-5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-300">
                        <RefreshCcw className="w-5 h-5" />
                      </div>
                      <h3 className="text-xl font-black text-gray-900 dark:text-white">
                        Regenerate Invoice
                      </h3>
                    </div>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Add adjustments and regenerate payable amount.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-300 transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-5">
                  {/* Invoice Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-2xl border border-gray-200 dark:border-blue-gray-900 bg-gray-50 dark:bg-white/[0.04] p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                        Invoice No
                      </p>
                      <p className="mt-1 text-sm font-extrabold text-gray-900 dark:text-white">
                        {invoiceNo}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-gray-200 dark:border-blue-gray-900 bg-gray-50 dark:bg-white/[0.04] p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                        Period
                      </p>
                      <p className="mt-1 text-sm font-extrabold text-gray-900 dark:text-white">
                        {months[month]} {Year}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-gray-200 dark:border-blue-gray-900 bg-gray-50 dark:bg-white/[0.04] p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                        Member
                      </p>
                      <p className="mt-1 text-sm font-extrabold text-gray-900 dark:text-white truncate">
                        {memberName}
                      </p>
                    </div>
                  </div>

                  {errorMsg && (
                    <div className="rounded-2xl border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 p-4 text-sm text-rose-700 dark:text-rose-200 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-bold">Couldn’t regenerate invoice</div>
                        <div className="opacity-90 mt-0.5 break-words">{errorMsg}</div>
                      </div>
                    </div>
                  )}

                  {/* Add Adjustment */}
                  <div className="rounded-3xl border border-gray-200 dark:border-blue-gray-900 bg-white dark:bg-white/[0.03] p-4">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3 items-end">
                      <div>
                        <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                          Change type
                        </label>
                        <select
                          className="w-full h-[46px] px-4 bg-gray-50 dark:bg-black/40 border border-gray-200 dark:border-blue-gray-900 rounded-2xl text-sm font-semibold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20"
                          value={selectedChangeKey}
                          onChange={(e) => setSelectedChangeKey(e.target.value)}
                        >
                          <option value="0">Select change…</option>
                          {availableChanges.map((c) => (
                            <option key={c.id} value={c.key}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                          Amount
                        </label>
                        <input
                          type="number"
                          className="w-full h-[46px] px-4 bg-gray-50 dark:bg-black/40 border border-gray-200 dark:border-blue-gray-900 rounded-2xl text-sm font-semibold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="e.g. 5000"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleAddEntry}
                        disabled={!canAdd}
                        className="h-[46px] inline-flex items-center justify-center gap-2 px-5 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-black text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Entries */}
                  <div className="rounded-3xl border border-gray-200 dark:border-blue-gray-900 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-white/[0.04]">
                      <div className="text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                        Adjustments
                      </div>
                      <div className="text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                        Total: {totalDelta.toLocaleString()}
                      </div>
                    </div>

                    {entries.length > 0 ? (
                      <div className="divide-y divide-gray-100 dark:divide-gray-800">
                        {entries.map((entry, index) => (
                          <div
                            key={`${entry.change}-${index}`}
                            className="flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-black/20"
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-extrabold text-gray-900 dark:text-white truncate">
                                {changeLabelByKey(entry.change)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                Key: {String(entry.change)}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-sm font-black text-gray-900 dark:text-white tabular-nums">
                                {Number(entry.amount).toLocaleString()}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveEntry(index)}
                                className="p-2 rounded-xl hover:bg-rose-500/10 text-rose-600 dark:text-rose-300 transition-colors"
                                aria-label="Remove entry"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                        No adjustments added yet.
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 px-6 py-5 border-t border-gray-100 dark:border-blue-gray-900">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-5 py-2.5 rounded-2xl text-sm font-black text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                  >
                    Close
                  </button>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setIsConfirmOpen(true)}
                      disabled={!canRegenerate}
                      className="px-6 py-2.5 rounded-2xl text-sm font-black bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Review & Confirm
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Confirmation dialog */}
            <AnimatePresence>
              {isConfirmOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                >
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={() => setIsConfirmOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 18, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 18, scale: 0.98 }}
                    transition={{ type: "spring", damping: 24, stiffness: 320 }}
                    className="relative w-full max-w-md rounded-3xl border border-gray-200 dark:border-blue-gray-900 bg-white dark:bg-[#0c0c0c] shadow-2xl p-6"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-300">
                        <AlertTriangle className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-lg font-black text-gray-900 dark:text-white">
                          Confirm regeneration
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          This will regenerate invoice payable amount using the adjustments below.
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 rounded-2xl border border-gray-200 dark:border-blue-gray-900 bg-gray-50 dark:bg-white/[0.04] p-4 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500 dark:text-gray-400 font-semibold">Changes</span>
                        <span className="text-gray-900 dark:text-white font-black">
                          {entries.length}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3 mt-2">
                        <span className="text-gray-500 dark:text-gray-400 font-semibold">Total delta</span>
                        <span className="text-gray-900 dark:text-white font-black tabular-nums">
                          {totalDelta.toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                        {entries.map((e) => changeLabelByKey(e.change)).join(", ")}
                      </div>
                    </div>

                    <div className="mt-5 flex gap-3">
                      <button
                        type="button"
                        onClick={() => setIsConfirmOpen(false)}
                        className="flex-1 px-4 py-2.5 rounded-2xl text-sm font-black text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handleUpdateGenerate}
                        disabled={isLoading}
                        className="flex-1 px-4 py-2.5 rounded-2xl text-sm font-black bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <>
                            <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                            Processing…
                          </>
                        ) : (
                          "Confirm"
                        )}
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
